import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { cn } from "@/lib/utils";

import { Plus, Trash2, Search } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/modals/confirm-modal";
import {
  useDeleteTreatment,
  getGetTreatmentsQueryOptions,
} from "@/requests/gen/react-query/treatment";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import TreatmentForm from "./-form";
import { Header } from "@/components/universal/header";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";

export const Route = createFileRoute("/_private/product/treatments/")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(getGetTreatmentsQueryOptions()),
  component: TreatmentsPage,
});

function TreatmentsPage() {
  const [search, setSearch] = useQueryState(
    "search",
    parseAsString.withDefault(""),
  );
  const [treatmentId, setTreatmentId] = useQueryState(
    "treatment_id",
    parseAsInteger.withOptions({ history: "push" }),
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const treatments = useSuspenseQuery(
    suspenseOptions(getGetTreatmentsQueryOptions()),
  );

  // Data is searched & filtered locally when typed
  const filteredTreatments = treatments.data?.filter((treatment) =>
    treatment.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedTreatment = treatments.data?.find(
    (treatment) => treatment.treatment_id === treatmentId,
  );

  // Delete mutation
  const deleteMutation = useDeleteTreatment();
  function onDeleteTreatment() {
    if (!treatmentId) {
      return;
    } else {
      deleteMutation.mutate(
        { treatmentId },
        {
          onSuccess: () => {
            treatments.refetch();
            setConfirmDialogOpen(false);
            setTreatmentId(null);
          },
        },
      );
    }
  }

  return (
    <div>
      <title>Treatments - Legrande Health Portal</title>
      {/* Confirm Delete Dialog */}
      <ConfirmModal
        isOpen={confirmDialogOpen}
        text={`Are you sure you want to delete "${selectedTreatment?.name}" treatment?`}
        title={`Delete ${selectedTreatment?.name}`}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          onDeleteTreatment();
        }}
        img="delete"
        cancelText="Cancel"
        confirmText="Delete"
      />
      <Header
        crumbs={[{ name: "Treatments", link: { to: "/product/treatments" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Treatments</CardTitle>
          <CardDescription>
            Manage treatments. Search, edit, and create all on this page.
          </CardDescription>
          <CardAction>
            <Button size="sm" onClick={() => setTreatmentId(0)}>
              <Plus />
              New Treatment
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          <div className="flex">
            {/* Left Navigation */}
            <div
              className={cn(
                "w-full md:w-1/4 md:border-r md:pr-3 space-y-2",
                treatmentId !== null && "hidden md:block",
              )}
            >
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  placeholder="Search"
                  className="pl-8"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                />
              </div>

              {/* Treatment list */}
              <ScrollArea>
                <ul className="flex flex-col h-96 space-y-1">
                  {filteredTreatments?.map((treatment) => {
                    return (
                      <li
                        key={treatment.treatment_id}
                        className={cn(
                          "rounded-md text-sm font-medium cursor-pointer px-3 py-2 transition-colors hover:bg-accent/50",
                          selectedTreatment?.treatment_id ===
                            treatment.treatment_id
                            ? "bg-accent text-accent-foreground font-semibold"
                            : "text-foreground",
                        )}
                        onClick={() => {
                          setTreatmentId(treatment.treatment_id);
                        }}
                      >
                        {treatment.name}
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>

            {/* Create/edit treatment form */}
            <div
              className={cn(
                "w-full md:w-3/4 py-3 md:pl-5",
                treatmentId === null && "hidden md:block",
              )}
            >
              <div className="flex flex-row justify-between items-center">
                <h4 className="text-xl font-semibold leading-none tracking-tight h-6">
                  {selectedTreatment ? "Update Treatment" : "New Treatment"}
                </h4>
                {selectedTreatment && (
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip="Delete"
                    onClick={() => setConfirmDialogOpen(true)}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
              <div className="flex items-center h-full">
                <TreatmentForm
                  key={selectedTreatment?.treatment_id}
                  treatment={selectedTreatment ?? undefined}
                  onSuccessFn={() => {
                    treatments.refetch();
                    setTreatmentId(null);
                  }}
                  onCancelBtn={
                    <Button
                      variant="outline"
                      onClick={() => setTreatmentId(null)}
                    >
                      Cancel
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
